/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import SubjectType from '@linc-technologies/linced-common/enums/subject-type';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';

@classNames('goals-next-steps')
export default class GoalsNextSteps extends Component {
  @action
  setMode(value) {
    this.mode = value;
  }

  // Services
  @service
  features;

  @service
  options;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @service
  strings;

  // to launch the next steps picker for a specific subject and category and
  // without additional navigation options pass mode and launch to be subject
  @tracked mode = 'current';

  launch = 'person-card';
  @tracked
  loadingStates = true;
  @tracked
  loadingSubjects = true;

  @or('loadingStates', 'loadingSubjects')
  loading;

  @optionAlias('hero:lock:milestones')
  milestonesLocked;
  @tracked
  loadingPlpCategories = true;
  @tracked
  loadingPlpSubcategories = true;
  @tracked
  loadingPlpGoals = true;
  @tracked
  loadingPlpStates = true;
  selectedCategory = null;

  @or(
    'loadingPlpCategories',
    'loadingPlpSubcategories',
    'loadingPlpGoals',
    'loadingPlpStates',
  )
  loadingPlp;

  @observes('mode')
  _loadPlp() {
    // Reset PLP loading states when changing display mode.
    this.loadingPlpCategories = true;
    this.loadingPlpSubcategories = true;
    this.loadingPlpGoals = true;
    this.loadingPlpStates = true;

    // If new filter is PLP, load the data.
    if (this.mode === 'plp') {
      this.loadPlp();
    }
  }

  @tracked
  loadingSubjectCategories = true;
  @tracked
  loadingSubjectSubcategories = true;
  @tracked
  loadingSubjectGoals = true;
  @tracked
  loadingSubjectStates = true;

  @or(
    'loadingSubjectCategories',
    'loadingSubjectSubcategories',
    'loadingSubjectGoals',
    'loadingSubjectStates',
  )
  loadingSubject;

  @observes('mode', 'subject.id')
  _loadSubject() {
    // Reset Subject loading states when changing display mode.
    this.loadingSubjectCategories = true;
    this.loadingSubjectSubcategories = true;
    this.loadingSubjectGoals = true;
    this.loadingSubjectStates = true;

    // If new filter is Subject, load the data.
    if (this.mode === 'subject' && !isEmpty(this.subject)) {
      this.loadSubject();
    }
  }

  @computed('states.[]', 'cache.[]', 'mode')
  get current() {
    let isPLP = this.mode === 'plp';
    let states = this.states.filter((state) => {
      let isDeveloping = state.get('awarded') === GoalState.DEVELOPING;
      if (isPLP) {
        return isDeveloping && state.get('goal.isPLP') === true;
      } else {
        return isDeveloping;
      }
    });

    if (!isPLP && this.cache.length > 0) {
      states.push(...this.cache);
    }

    return states.sort((a, b) => {
      let aGoal = a.get('goal');
      let bGoal = b.get('goal');
      return (
        (aGoal.get('subject.label') || '').localeCompare(
          bGoal.get('subject.label') || '',
        ) ||
        aGoal.get('category.sequence') - bGoal.get('category.sequence') ||
        aGoal.get('subcategory.sequence') - bGoal.get('subcategory.sequence') ||
        aGoal.get('sequence') - bGoal.get('sequence')
      );
    });
  }

  @tracked
  cache = [];

  @tracked
  plpCategories;

  @tracked
  plpGoals;

  @tracked
  plpStates;

  @tracked
  plpSubcategories;

  @tracked
  states;

  @tracked
  subjectCategories;

  @tracked
  subjectGoals;

  @tracked
  subjectStates;

  @tracked
  subjectSubcategories;

  @tracked
  subjects;

  init() {
    super.init(...arguments);

    let store = this.store;
    store
      .query('goals/state', {
        personId: this.get('person.id'),
        current: true,
        include: 'goals',
      })
      .then((states) => {
        this.states = states;
        this.loadingStates = false;
      });
    store
      .query('goals/subject', { visibleToTeachers: true })
      .then((subjects) => {
        let filteredSubjects = subjects.filter((subject) => {
          if (subject.isTopic) {
            // Filter out subjects which are Topics rather than Goals.
            return false;
          }
          if (isEmpty(subject.subjectType)) {
            // Include subjects which haven't got a specific type set.
            return true;
          }
          // Include subjects which have content (goals).
          let subjectType = SubjectType.getObject(subject.subjectType);
          return isEmpty(subjectType) || subjectType.hasContent;
        });

        // Sort the filtered subjects by 'label'
        this.subjects = filteredSubjects.sort(byKeys('label'));
      })
      .finally(() => {
        this.loadingSubjects = false;
      });
    this.cache = [];
    if (this.mode === 'subject' && !isEmpty(this.subject)) {
      this.loadSubject();
    }
  }

  loadPlp() {
    let store = this.store;

    store.query('goals/category', { isPLP: true }).then((categories) => {
      this.plpCategories = categories;
      this.loadingPlpCategories = false;
    });
    store.query('goals/subcategory', { isPLP: true }).then((subcategories) => {
      this.plpSubcategories = subcategories;
      this.loadingPlpSubcategories = false;
    });
    store
      .query('goals/goal', { personId: this.get('person.id') })
      .then((goals) => {
        this.plpGoals = goals;
        this.loadingPlpGoals = false;
      });
    store
      .query('goals/state', {
        isPLP: true,
        personId: this.get('person.id'),
        current: true,
      })
      .then((states) => {
        this.plpStates = states;
        this.loadingPlpStates = false;
      });
  }

  loadSubject() {
    let store = this.store;
    let subject = this.subject;

    store
      .query('goals/category', { subjectId: subject.id })
      .then((categories) => {
        this.subjectCategories = categories;
        this.loadingSubjectCategories = false;
      });
    store
      .query('goals/subcategory', { subjectId: subject.id })
      .then((subcategories) => {
        this.subjectSubcategories = subcategories;
        this.loadingSubjectSubcategories = false;
      });
    store.query('goals/goal', { subjectId: subject.id }).then((goals) => {
      this.subjectGoals = goals;
      this.loadingSubjectGoals = false;
    });
    store
      .query('goals/state', {
        subjectId: subject.id,
        personId: this.get('person.id'),
        current: true,
      })
      .then((states) => {
        this.subjectStates = states;
        this.loadingSubjectStates = false;
      });
  }

  updateCache(newState) {
    // Remove from the cache if it's currently there, irrespective of new state.
    let existing = findBy(this.cache, 'goal.id', newState.get('goal.id'));
    if (!isEmpty(existing)) {
      this.cache = this.cache.filter((state) => state !== existing);
    }
    if (newState.awarded !== GoalState.DEVELOPING) {
      // Push goal state into the cache so that we can keep displaying it after the state change.
      this.cache = [...this.cache, newState];
    }
  }

  closeCard() {}

  @action
  goToRoute(route) {
    this.closeCard();
    this.router.transitionTo(`people.person.${route}`, this.get('person.id'));
  }

  @action
  setGoalSubject(subject) {
    this.setProperties({
      subject,
      mode: 'subject',
    });
  }

  @action
  viewCurrent() {
    this.setProperties({
      cache: [],
      mode: 'current',
      subject: null,
    });
    this.states.update();
  }

  @action
  updateGrade(state) {
    this.states.update().then(() => {
      this.updateCache(state);
    });
  }

  @action
  async updateState(goal, previousState, grade) {
    let awarded = GoalState.DEVELOPING;

    switch (previousState) {
      case GoalState.DEVELOPING:
        awarded = GoalState.SECURE;
        break;
      case GoalState.SECURE:
        awarded = GoalState.NOT_SET;
        break;
    }

    let stateParams = {
      schoolId: this.get('session.school'),
      assessorId: this.get('session.person'),
      personId: this.person,
      // The goal card can give the async proxy. WarpDrive accepts only a record.
      goalId: await goal,
      awarded,
    };

    if (!isEmpty(grade)) {
      stateParams.grade = grade;
    }
    let newState = this.store.createRecord('goals/state', stateParams);
    saveOrDiscard(newState)
      .then(() => {
        this.states.update().then(() => {
          this.updateCache(newState);
        });
      })
      .catch(() => this.toaster.error('Error saving the goal state.'));
  }

  @action
  refreshStates() {
    if (!isEmpty(this.plpStates)) {
      this.plpStates.update();
    }
    if (!isEmpty(this.subjectStates)) {
      this.subjectStates.update();
    }
  }

  @action
  reloadPlp() {
    this.loadPlp();
  }
}
